import { KolorcansAssemblyFamily } from './kolorcans-data.model';
import { PlasticsOrderedProductStatus } from '../enums/plastics-ordered-product-status.enum';

export const KOLORCANS_PACKING_CONFIG_SCHEMA_VERSION = 1;
export const KOLORCANS_PACKING_ALGORITHM_VERSION = 1;
export const KOLORCANS_PACKING_PLAN_ELIGIBLE_ORDERED_PRODUCT_STATUSES: ReadonlySet<PlasticsOrderedProductStatus> = new Set([
    PlasticsOrderedProductStatus.Draft,
    PlasticsOrderedProductStatus.Released,
    PlasticsOrderedProductStatus.Hold,
    PlasticsOrderedProductStatus.Scheduled,
]);

export function isKolorcansPackingPlanEligibleOrderedProductStatus(
    status: PlasticsOrderedProductStatus,
): boolean {
    return KOLORCANS_PACKING_PLAN_ELIGIBLE_ORDERED_PRODUCT_STATUSES.has(status);
}

export interface KolorcansPackingConfig {
    id: string;
    orderedProductIds: string[];
    payloadJson: string;
    schemaVersion: number;
    packingAlgorithmVersion: number;
    readyForDashboard: boolean;
    archived: boolean;
    createdOn: Date;
    createdById: string;
    updatedOn: Date;
    updatedById: string;
}

export interface KolorcansPackingConfigProduct {
    id: string;
    packingConfigId: string;
}

export interface KolorcansPackingConfigPayloadV1 {
    maxPalletHeight: number;
    manualPallets?: KolorcansPersistedPallet[];
    shipInBoxesOrderedProductIds?: string[];
    assemblyGroups: KolorcansPersistedAssemblyGroup[];
    boxes: KolorcansPersistedBox[];
}

export interface KolorcansPersistedPallet {
    id: string;
}

export type KolorcansBoxOrientation =
    | 'upright'
    | 'upright-turned'
    | 'length-side'
    | 'length-side-turned'
    | 'width-side'
    | 'width-side-turned';

export const KOLORCANS_BOX_ORIENTATIONS: readonly KolorcansBoxOrientation[] = [
    'upright', 'upright-turned', 'length-side', 'length-side-turned', 'width-side', 'width-side-turned',
];

export interface KolorcansPersistedAssemblyGroup {
    id: string;
    orderedProductIds: string[];
    family: KolorcansAssemblyFamily | '';
}

export interface KolorcansPersistedBox {
    id: string;
    boxTypeId: string;
    label: string;
    color: string;
    isManual: boolean;
    autoGenerated?: boolean;
    assemblyGroupId?: string;
    orientation?: KolorcansBoxOrientation;
    palletId?: string;
    dimensions: {
        length: number;
        width: number;
        height: number;
    };
    items: KolorcansPersistedBoxItem[];
}

export interface KolorcansPersistedBoxItem {
    id: string;
    orderedProductId: string;
    bomLineItemId: string;
    productId: string;
    productLabel: string;
    thumbnailImageUrl: string;
    salesOrderNumber: string;
    purchaseOrderNumber: string;
    quantity: number;
}

export function parseKolorcansPackingConfigPayload(
    config: Pick<KolorcansPackingConfig, 'payloadJson' | 'schemaVersion' | 'packingAlgorithmVersion'>,
): KolorcansPackingConfigPayloadV1 {
    if (config.schemaVersion !== KOLORCANS_PACKING_CONFIG_SCHEMA_VERSION) {
        throw new Error(`Unsupported KOLORCANS packing config schema version ${config.schemaVersion}.`);
    }
    if (config.packingAlgorithmVersion !== KOLORCANS_PACKING_ALGORITHM_VERSION) {
        throw new Error(`Unsupported KOLORCANS packing algorithm version ${config.packingAlgorithmVersion}.`);
    }

    const payload = JSON.parse(config.payloadJson) as Partial<KolorcansPackingConfigPayloadV1>;
    if (!Number.isFinite(payload.maxPalletHeight)
        || !Array.isArray(payload.assemblyGroups)
        || !Array.isArray(payload.boxes)
        || (payload.shipInBoxesOrderedProductIds !== undefined
            && (!Array.isArray(payload.shipInBoxesOrderedProductIds)
                || payload.shipInBoxesOrderedProductIds.some(id => typeof id !== 'string')))) {
        throw new Error('The KOLORCANS packing config payload is invalid.');
    }

    if (payload.manualPallets !== undefined
        && (!Array.isArray(payload.manualPallets)
            || payload.manualPallets.some(pallet => !pallet || typeof pallet.id !== 'string' || !pallet.id)
            || new Set(payload.manualPallets.map(pallet => pallet.id)).size !== payload.manualPallets.length)) {
        throw new Error('The KOLORCANS packing config skid assignments are invalid.');
    }
    const palletIds = new Set(payload.manualPallets?.map(pallet => pallet.id));
    if (payload.boxes.some(box => (box.orientation !== undefined && !KOLORCANS_BOX_ORIENTATIONS.includes(box.orientation))
        || (box.palletId !== undefined && !palletIds.has(box.palletId)))) {
        throw new Error('The KOLORCANS packing config box orientation or skid assignment is invalid.');
    }

    return payload as KolorcansPackingConfigPayloadV1;
}
